import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/utils";
import * as React from "react";
import type { FieldError as RHFFieldError } from "react-hook-form";

const Field = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("space-y-1 relative", className)} {...props} />
  );
});
Field.displayName = "Field";

const FieldLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => {
  return <Label ref={ref} className={className} {...props} />;
});
FieldLabel.displayName = "FieldLabel";

interface FieldErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {
  errors?: (RHFFieldError | undefined)[];
}

const FieldError = React.forwardRef<HTMLParagraphElement, FieldErrorProps>(
  ({ className, errors, ...props }, ref) => {
    const error = errors?.find((e) => e);

    if (!error?.message) {
      return null;
    }

    return (
      <p
        ref={ref}
        className={cn("text-[10px] font-medium mt-1 text-error", className)}
        {...props}
      >
        {String(error.message)}
      </p>
    );
  },
);
FieldError.displayName = "FieldError";

const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn("text-[10px] font-medium text-tertiary mt-1", className)}
      {...props}
    />
  );
});
FieldDescription.displayName = "FieldDescription";

const FieldGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex flex-col gap-3", className)}
      {...props}
    />
  );
});
FieldGroup.displayName = "FieldGroup";

export { Field, FieldLabel, FieldError, FieldDescription, FieldGroup };
